import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Baby, GraduationCap, Sparkle, ShieldCheck, ChatCircleText, ChartLine,
  Buildings, ShoppingBag, Heart, ForkKnife, Calendar, CurrencyCircleDollar,
  ArrowUpRight, CheckCircle, Globe, Lightning, Path, WhatsappLogo, EnvelopeSimple, MapPin, Phone,
} from "@phosphor-icons/react";
import { FACTSKOOT_WA_NUMBER, FACTSKOOT_WA_DISPLAY, FACTSKOOT_EMAIL, WhatsAppLink } from "@/components/FloatingWhatsApp";
import { Logo } from "@/components/Logo";
import { isLiveMode } from "@/lib/envMode";
import { useSiteAnalytics } from "@/lib/useSiteAnalytics";

export default function Landing() {
  useSiteAnalytics();
  const { t } = useTranslation();

  const features = [
    { icon: Baby,                   key: "1", tone: "terracotta" },
    { icon: ChartLine,              key: "2", tone: "sage" },
    { icon: CurrencyCircleDollar,   key: "3", tone: "mustard" },
    { icon: ShieldCheck,            key: "4", tone: "lavender" },
    { icon: Buildings,              key: "5", tone: "terracotta" },
    { icon: Sparkle,                key: "6", tone: "sage" },
    { icon: ShoppingBag,            key: "7", tone: "mustard" },
    { icon: ChatCircleText,         key: "8", tone: "lavender" },
  ];

  const moduleList = [
    t("nav.enquiries"), t("admission.title"), t("fees.title"), t("dashboards.parentTitle", { defaultValue: "Parent dashboard" }),
    t("dashboards.teacherTitle", { defaultValue: "Teacher dashboard" }), t("dashboards.adminTitle", { defaultValue: "Admin dashboard" }),
    t("nav.apparel"), t("nav.dailyUpdates"),
    t("attendance.title"), t("nav.pickup"), t("nav.communications"), t("nav.curriculum"),
    t("nav.digiBoard"), t("nav.behaviour"), t("nav.health"), t("nav.meals"),
    t("nav.activities"), t("nav.franchiseHq"), t("nav.marketing"), "MangoCubs AI",
  ];

  const testimonials = [
    { quoteKey: "t1", authorKey: "a1", emoji: "🌸" },
    { quoteKey: "t2", authorKey: "a2", emoji: "🎈" },
    { quoteKey: "t3", authorKey: "a3", emoji: "🪁" },
  ];

  const stats = [
    { k: "8,200+", v: t("landing.statUpdatesLabel") },
    { k: "73%",    v: t("landing.statConversionLabel") },
    { k: "12 min", v: t("landing.statAdmitLabel") },
    { k: "4.7 / 5", v: t("landing.statSatLabel") },
  ];

  return (
    <div className="min-h-screen bg-paper text-ink overflow-x-hidden">
      <Nav />
      <Hero />
      <SectionFeatures />
      <SectionDashboards />
      <SectionModulesGrid />
      <SectionFranchise />
      <SectionAI />
      {!isLiveMode() && <SectionTestimonials />}
      <SectionContact />
      <SectionCTA />
      <Footer />
    </div>
  );

  function Nav() {
    return (
      <header className="sticky top-0 z-30 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-brand">
            <Logo size={42} wordmark={true} tagline={false} />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-ink-soft">
            <a href="#features" className="hover:text-ink">{t("landing.navFeatures")}</a>
            <a href="#franchise" className="hover:text-ink">{t("landing.navFranchise")}</a>
            <a href="#ai" className="hover:text-ink">MangoCubs AI</a>
            <a href="#modules" className="hover:text-ink">{t("landing.navModules")}</a>
            <a href="#contact" className="hover:text-ink">{t("landing.navContact")}</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/demo" className="hidden md:inline-flex text-sm font-medium px-4 py-2 rounded-full hover:bg-paper-alt" data-testid="landing-enquiry-link">
              {t("landing.getDemo")}
            </Link>
            <Link to="/login" className="text-sm font-semibold px-4 py-2 rounded-full bg-ink text-white hover:bg-ink-soft transition-colors" data-testid="landing-login-link">
              {t("landing.signIn")}
            </Link>
          </div>
        </div>
      </header>
    );
  }

  function Hero() {
    return (
      <section className="relative pt-12 lg:pt-20 pb-16 lg:pb-28">
        <div className="absolute -top-24 -left-32 w-96 h-96 rounded-full bg-mustard/30 blur-3xl pointer-events-none" />
        <div className="absolute top-40 -right-24 w-80 h-80 rounded-full bg-terracotta/20 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-12 gap-10 items-center relative">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-border text-xs font-semibold text-ink-soft shadow-soft">
              <span className="w-1.5 h-1.5 rounded-full bg-sage-deep" /> {t("landing.heroBadge")}
            </div>
            <h1 className="mt-6 font-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-balance">
              {t("landing.heroH1Pre")} <span className="wavy-underline">{t("landing.heroH1Highlight")}</span> {t("landing.heroH1Post")}
            </h1>
            <p className="mt-6 text-lg text-ink-soft max-w-xl">
              {t("landing.heroSub")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/demo" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover shadow-floating transition-all hover:-translate-y-0.5" data-testid="hero-cta-demo">
                {t("landing.bookDemo")} <ArrowUpRight weight="bold" size={18} />
              </Link>
              <WhatsAppLink message="Hi! I'd love to learn more about MangoCubs Preschool OS." className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-border text-ink font-semibold hover:bg-paper-alt transition-all" data-testid="hero-cta-whatsapp">
                <WhatsappLogo weight="fill" className="text-[#25D366]" /> {t("landing.ctaWhatsapp")}
              </WhatsAppLink>
            </div>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl">
              {stats.map((s) => (
                <div key={s.v} className="rounded-2xl bg-white border border-border p-4">
                  <div className="font-heading font-bold text-2xl tracking-tight text-ink">{s.k}</div>
                  <div className="text-xs text-ink-soft mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="lg:col-span-5 relative">
            <div className="relative rounded-[2rem] overflow-hidden shadow-floating border border-border bg-white">
              <img
                src="https://images.unsplash.com/photo-1759678444870-1f09f0d9e688?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHxoYXBweSUyMHByZXNjaG9vbCUyMGNoaWxkJTIwbGVhcm5pbmd8ZW58MHx8fHwxNzc4NDgwNDE0fDA&ixlib=rb-4.1.0&q=85"
                alt="Happy preschool child learning"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-x-4 bottom-4 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-white/95 backdrop-blur p-4 border border-border">
                  <div className="text-xs text-ink-soft">{t("landing.todaySunflower")}</div>
                  <div className="font-heading font-semibold mt-1">{t("landing.rainbowMsg")} 🎨</div>
                </div>
                <div className="rounded-2xl bg-terracotta text-white p-4">
                  <div className="text-xs opacity-80">{t("landing.conversionMonth")}</div>
                  <div className="font-heading font-semibold text-2xl mt-1">73%</div>
                </div>
              </div>
            </div>
            <div className="absolute -top-4 -right-4 hidden md:flex items-center gap-2 px-3 py-2 rounded-2xl bg-mustard text-ink font-semibold shadow-sun">
              <Sparkle weight="duotone" /> {t("landing.aiOn")}
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  function TrustedStrip() {
    const brands = ["Kidzee", "EuroKids", "Podar Jumbo Kids", "Little Millennium", "KLAY", "Bachpan"];
    return (
      <section className="border-y border-border bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-ink-soft text-sm font-medium">
          <span className="text-xs uppercase tracking-[0.18em] font-semibold mr-2">{t("landing.trustedBy")}</span>
          {brands.map((b) => <span key={b} className="font-heading font-semibold">{b}</span>)}
        </div>
      </section>
    );
  }

  function SectionFeatures() {
    return (
      <section id="features" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-mustard-hover">{t("landing.featuresKicker")}</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-3 leading-tight">{t("landing.featuresH2")}</h2>
            <p className="text-ink-soft mt-4 text-lg">
              {t("landing.featuresSub")}
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f, i) => (
              <motion.div
                key={f.key}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-white rounded-3xl border border-border p-6 shadow-soft hover:shadow-hoverlift hover:-translate-y-1 transition-all"
              >
                <span className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-${f.tone}-soft text-${f.tone}${f.tone === "mustard" ? "-hover" : f.tone === "sage" ? "-deep" : f.tone === "lavender" ? "-deep" : ""}`}>
                  <f.icon size={24} weight="duotone" />
                </span>
                <h3 className="mt-4 font-heading font-semibold text-lg">{t(`landing.feat${f.key}Title`)}</h3>
                <p className="text-ink-soft text-sm mt-2 leading-relaxed">{t(`landing.feat${f.key}Body`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  function SectionDashboards() {
    return (
      <section className="py-20 lg:py-28 bg-white border-y border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-sage-deep">{t("landing.dashboardsKicker")}</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-3 leading-tight">{t("landing.dashboardsH2")}</h2>
            <p className="text-ink-soft mt-4 text-lg">
              {t("landing.dashboardsSub")}
            </p>
            <ul className="mt-6 space-y-3">
              {[
                t("landing.roleSuperAdmin"),
                t("landing.roleBranch"),
                t("landing.roleTeacher"),
                t("landing.roleParent"),
              ].map((txt) => (
                <li key={txt} className="flex items-center gap-3"><CheckCircle weight="fill" className="text-sage-deep" /> <span className="text-ink">{txt}</span></li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            <img className="rounded-3xl h-full object-cover shadow-soft border border-border col-span-2 lg:row-span-2" src="https://images.unsplash.com/photo-1770096679916-2cd9c720d400?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxraW5kZXJnYXJ0ZW4lMjB0ZWFjaGVyJTIwaGVscGluZyUyMGNoaWxkfGVufDB8fHx8MTc3ODQ4MDQxNXww&ixlib=rb-4.1.0&q=85" alt="Teacher with children" />
            <img className="rounded-3xl shadow-soft border border-border object-cover" src="https://images.unsplash.com/photo-1691256257499-25b0717e3f57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHByZXNjaG9vbCUyMGNoaWxkJTIwbGVhcm5pbmd8ZW58MHx8fHwxNzc4NDgwNDE0fDA&ixlib=rb-4.1.0&q=85" alt="Child" />
            <img className="rounded-3xl shadow-soft border border-border object-cover" src="https://images.unsplash.com/photo-1761604478724-13fe879468cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxraW5kZXJnYXJ0ZW4lMjB0ZWFjaGVyJTIwaGVscGluZyUyMGNoaWxkfGVufDB8fHx8MTc3ODQ4MDQxNXww&ixlib=rb-4.1.0&q=85" alt="Admin view" />
          </div>
        </div>
      </section>
    );
  }

  function SectionModulesGrid() {
    return (
      <section id="modules" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div className="max-w-xl">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-lavender-deep">{t("landing.modulesKicker")}</div>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-3 leading-tight">{t("landing.modulesH2")}</h2>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {moduleList.map((m, i) => (
              <span key={`${m}-${i}`} className="px-4 py-2 rounded-full bg-white border border-border text-sm font-medium text-ink hover:bg-paper-alt transition-colors">{m}</span>
            ))}
          </div>
        </div>
      </section>
    );
  }

  function SectionFranchise() {
    return (
      <section id="franchise" className="py-20 lg:py-28 bg-paper-alt">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-terracotta">{t("landing.franchiseKicker")}</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-3 leading-tight">{t("landing.franchiseH2")}</h2>
            <p className="text-ink-soft mt-4 text-lg">
              {t("landing.franchiseSub")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-border"><Globe weight="duotone" /> {t("landing.multiCountry")}</span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-border"><CurrencyCircleDollar weight="duotone" /> {t("landing.multiCurrency")}</span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-border"><Path weight="duotone" /> {t("landing.sopReady")}</span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-border"><Lightning weight="duotone" /> {t("landing.whiteLabel")}</span>
            </div>
          </div>
          {/* Right column — "founding partner" CTA. MangoCubs is a new
              product with no live customer centres yet, so we never show
              the playful Bangalore/Mumbai/Singapore demo tiles publicly. */}
          <div className="rounded-[2rem] p-8 lg:p-10 bg-gradient-to-br from-mustard/40 to-terracotta/20 border border-border">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-terracotta">Now onboarding</div>
            <div className="font-heading text-3xl sm:text-4xl font-bold mt-2 leading-tight">Become our founding partner</div>
            <p className="text-ink-soft mt-3">Be among the first preschool chains to launch on MangoCubs. White-label branding, founding-member pricing, and our team setting up your first branch alongside you.</p>
            <Link to="/demo" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover transition" data-testid="founding-partner-cta">
              Talk to us <ArrowUpRight weight="bold" size={16} />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  function SectionAI() {
    return (
      <section id="ai" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-[2rem] p-8 bg-ink text-white relative overflow-hidden">
            <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-terracotta/40 rounded-full blur-3xl" />
            <Sparkle size={28} weight="duotone" className="text-mustard" />
            <h3 className="mt-3 font-heading text-3xl font-bold">MangoCubs AI</h3>
            <p className="mt-3 opacity-80">{t("landing.aiCardSub", { defaultValue: "A gentle, parent-friendly assistant. Built for teachers." })}</p>
            <div className="mt-6 space-y-3">
              {[
                t("landing.aiPrompt1", { defaultValue: "Generate a worksheet on rainy day animals — UKG" }),
                t("landing.aiPrompt2", { defaultValue: "Write a calm message to Diya's parents about her cold" }),
                t("landing.aiPrompt3", { defaultValue: "Draft this week's lesson plan around 'Tiny Helpers'" }),
                t("landing.aiPrompt4", { defaultValue: "Spot attendance patterns I should worry about" }),
              ].map((tx) => (
                <div key={tx} className="px-4 py-3 rounded-2xl bg-white/10 border border-white/15">{tx}</div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-mustard-hover">{t("landing.aiKicker")}</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-3 leading-tight">{t("landing.aiH2")}</h2>
            <p className="text-ink-soft mt-4 text-lg">
              {t("landing.aiSub")}
            </p>
          </div>
        </div>
      </section>
    );
  }

  function SectionTestimonials() {
    return (
      <section className="py-20 lg:py-28 bg-white border-y border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold leading-tight max-w-3xl">{t("landing.testimonialsH2")}</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {testimonials.map((tt) => (
              <div key={tt.quoteKey} className="bg-paper rounded-3xl border border-border p-6">
                <div className="text-3xl">{tt.emoji}</div>
                <p className="mt-4 font-heading text-lg leading-snug">"{t(`landing.testimonial.${tt.quoteKey}`, { defaultValue: "—" })}"</p>
                <div className="mt-4 text-sm text-ink-soft">— {t(`landing.testimonial.${tt.authorKey}`, { defaultValue: "—" })}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  function SectionContact() {
    return (
      <section id="contact" className="py-20 lg:py-28 bg-paper-alt">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-sage-deep">{t("landing.contactKicker")}</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-3 leading-tight">{t("landing.contactH2")}</h2>
            <p className="text-ink-soft mt-4 text-lg max-w-lg">
              {t("landing.contactSub")}
            </p>
            <div className="mt-8 grid gap-3 max-w-md">
              <WhatsAppLink
                message="Hi! I'd like to know more about MangoCubs Preschool OS."
                className="flex items-center gap-4 p-4 rounded-2xl bg-[#25D366] text-white hover:bg-[#1DA851] transition shadow-floating"
                data-testid="contact-whatsapp"
              >
                <WhatsappLogo size={28} weight="fill" />
                <div>
                  <div className="text-xs uppercase tracking-wider opacity-90">WhatsApp</div>
                  <div className="font-heading font-bold text-lg leading-tight">{FACTSKOOT_WA_DISPLAY}</div>
                </div>
                <ArrowUpRight className="ml-auto" weight="bold" />
              </WhatsAppLink>
              <a
                href={`mailto:${FACTSKOOT_EMAIL}`}
                className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-border hover:bg-paper transition"
                data-testid="contact-email"
              >
                <span className="w-12 h-12 rounded-xl bg-terracotta-soft text-terracotta flex items-center justify-center">
                  <EnvelopeSimple size={24} weight="duotone" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-ink-soft">{t("common.email")}</div>
                  <div className="font-heading font-bold text-lg leading-tight">{FACTSKOOT_EMAIL}</div>
                </div>
                <ArrowUpRight className="ml-auto text-ink-soft" weight="bold" />
              </a>
              <Link
                to="/demo"
                className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-border hover:bg-paper transition"
                data-testid="contact-enquiry"
              >
                <span className="w-12 h-12 rounded-xl bg-mustard-soft text-mustard-hover flex items-center justify-center">
                  <ChatCircleText size={24} weight="duotone" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-ink-soft">{t("landing.scheduleDemo")}</div>
                  <div className="font-heading font-bold text-lg leading-tight">{t("landing.demo30min")}</div>
                </div>
                <ArrowUpRight className="ml-auto text-ink-soft" weight="bold" />
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-floating border border-border">
              <img
                src="https://images.unsplash.com/photo-1759678444870-1f09f0d9e688?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHxoYXBweSUyMHByZXNjaG9vbCUyMGNoaWxkJTIwbGVhcm5pbmd8ZW58MHx8fHwxNzc4NDgwNDE0fDA&ixlib=rb-4.1.0&q=85"
                alt="Preschool child"
                className="w-full h-[420px] object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 sm:left-6 right-6 sm:right-auto bg-white rounded-2xl p-5 border border-border shadow-hoverlift sm:w-72">
              <div className="text-xs uppercase tracking-wider text-ink-soft">{t("landing.officeHours")}</div>
              <div className="font-heading font-bold mt-1">{t("landing.officeHoursTime")}</div>
              <div className="text-sm text-ink-soft mt-1">{t("landing.officeHoursNote")}</div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  function SectionCTA() {
    return (
      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <div className="relative overflow-hidden rounded-[2rem] bg-terracotta text-white p-10 lg:p-16 text-center shadow-floating">
            <div className="absolute top-6 left-8 w-8 h-8 rounded-full bg-mustard/80 animate-float" />
            <div className="absolute bottom-10 right-12 w-10 h-10 rounded-full bg-white/30 animate-float [animation-delay:1s]" />
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">{t("landing.ctaH2")}</h2>
            <p className="mt-4 text-white/85 text-lg max-w-2xl mx-auto">{t("landing.ctaSub")}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/demo" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-terracotta font-semibold hover:bg-mustard-light transition-colors" data-testid="cta-contact">
                {t("landing.ctaSignIn")}
              </Link>
              <Link to="/demo" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ink text-white font-semibold hover:bg-black/90 transition-colors" data-testid="cta-demo">
                {t("landing.ctaDemo")}
              </Link>
              <WhatsAppLink message="Hi! Please book me a demo of MangoCubs Preschool OS." className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366] text-white font-semibold hover:bg-[#1DA851] transition-colors" data-testid="cta-whatsapp">
                <WhatsappLogo weight="fill" /> {t("landing.ctaWhatsapp")}
              </WhatsAppLink>
            </div>
          </div>
        </div>
      </section>
    );
  }

  function Footer() {
    return (
      <footer className="border-t border-border bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Logo size={40} wordmark={true} tagline={true} />
            </div>
            <p className="text-sm text-ink-soft max-w-xs mt-3">{t("landing.footerBlurb")}</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-ink mb-3">{t("landing.footerProduct")}</div>
            <ul className="space-y-2 text-sm text-ink-soft">
              <li><a href="#features" className="hover:text-ink">{t("landing.navFeatures")}</a></li>
              <li><a href="#modules" className="hover:text-ink">{t("landing.navModules")}</a></li>
              <li><a href="#franchise" className="hover:text-ink">{t("landing.navFranchise")}</a></li>
              <li><a href="#ai" className="hover:text-ink">MangoCubs AI</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-ink mb-3">{t("landing.footerGetStarted")}</div>
            <ul className="space-y-2 text-sm text-ink-soft">
              <li><Link to="/demo" className="hover:text-ink" data-testid="footer-enquire">{t("landing.footerEnquire")}</Link></li>
              <li><Link to="/admission" className="hover:text-ink">{t("landing.footerDigitalAdmit")}</Link></li>
              <li><Link to="/register" className="hover:text-ink">{t("landing.footerCreate")}</Link></li>
              <li><Link to="/login" className="hover:text-ink">{t("landing.signIn")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-ink mb-3">{t("landing.footerReachUs")}</div>
            <ul className="space-y-2 text-sm text-ink-soft">
              <li className="flex items-center gap-2">
                <WhatsappLogo weight="fill" className="text-[#25D366]" />
                <WhatsAppLink className="hover:text-ink" data-testid="footer-whatsapp">{FACTSKOOT_WA_DISPLAY}</WhatsAppLink>
              </li>
              <li className="flex items-center gap-2">
                <EnvelopeSimple weight="duotone" />
                <a href={`mailto:${FACTSKOOT_EMAIL}`} className="hover:text-ink" data-testid="footer-email">{FACTSKOOT_EMAIL}</a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-ink-soft">
            <div>{t("landing.footerCopyright", { year: new Date().getFullYear() })}</div>
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <Link to="/legal/privacy" className="hover:text-ink" data-testid="footer-privacy">{t("landing.footerPrivacy")}</Link>
              <Link to="/legal/terms" className="hover:text-ink" data-testid="footer-terms">{t("landing.footerTerms")}</Link>
              <Link to="/legal/refund" className="hover:text-ink" data-testid="footer-refund">{t("landing.footerRefund")}</Link>
              <Link to="/legal/child-privacy" className="hover:text-ink" data-testid="footer-child-privacy">{t("landing.footerChildPrivacy")}</Link>
            </div>
          </div>
        </div>
      </footer>
    );
  }
}
